import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, FormControl, InputLabel, Select, MenuItem, Button,
    TablePagination, Chip, Tooltip, Alert
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { ISalaryPayment, JournalPostStatus } from '../../interfaces/salaryPayment';
import { PERMISSIONS } from '../../constants/permissions';
import SkipJournalDialog from './SkipJournalDialog';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

type FilterStatus = 'ALL' | 'FAILED' | 'SKIPPED' | 'PENDING';

const STATUS_COLORS: Record<JournalPostStatus, 'default' | 'warning' | 'error' | 'success'> = {
    PENDING: 'warning',
    POSTED: 'success',
    FAILED: 'error',
    SKIPPED: 'default',
};

const truncate = (s: string | null | undefined, n = 80) => {
    if (!s) return '-';
    return s.length > n ? s.slice(0, n) + '…' : s;
};

const PaymentReconciliation: React.FC = () => {
    const [filter, setFilter] = useState<FilterStatus>('ALL');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [payments, setPayments] = useState<ISalaryPayment[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [skipTargetId, setSkipTargetId] = useState<string | null>(null);

    const { showSuccess, showError, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const canCreatePayment = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    const canOverride = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            if (filter === 'ALL') {
                // No status = every payment not yet posted (failed, skipped and pending).
                const res = await PayrollService.getPaymentReconciliation(institutionId, undefined, page, size);
                setPayments(res.content);
                setTotalElements(res.totalElements);
            } else {
                const res = await PayrollService.getPaymentReconciliation(institutionId, filter, page, size);
                setPayments(res.content);
                setTotalElements(res.totalElements);
            }
        } catch (error) {
            showApiError(error, 'Failed to load reconciliation data');
        } finally {
            setLoading(false);
        }
    }, [institutionId, filter, page, size, showApiError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleFilterChange = (v: FilterStatus) => {
        setFilter(v);
        setPage(0);
    };

    const handleRetry = async (paymentId: string) => {
        try {
            const result = await PayrollService.retryJournalPost(paymentId);
            if (result?.journalPostStatus && result.journalPostStatus !== 'POSTED') {
                // The API answers 200 even when the entry could not be posted (missing accounts etc.).
                showError(result.journalPostWarning || result.journalPostError
                    || 'The journal entry could not be posted. Check the account settings in Payroll Settings.');
            } else {
                showSuccess('Journal post retry succeeded');
            }
            load();
        } catch (error) {
            showApiError(error, 'Retry failed');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.recon.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="h5">Payment Reconciliation</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.recon.institution"
                        label="Select Institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                )}
                <FormControl data-help="payroll.recon.status" size="small" sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                    <InputLabel id="reconciliation-filter-label">Status</InputLabel>
                    <Select
                        labelId="reconciliation-filter-label"
                        label="Status"
                        value={filter}
                        onChange={(e) => handleFilterChange(e.target.value as FilterStatus)}
                        inputProps={{ 'aria-label': 'Status' }}
                    >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="FAILED">Failed</MenuItem>
                        <MenuItem value="SKIPPED">Skipped</MenuItem>
                        <MenuItem value="PENDING">Pending</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {payments.some(p => p.journalPostStatus === 'SKIPPED') && (
                <Alert data-help-section="payroll.recon.skippedNote" severity="info" sx={{ mb: 2 }}>
                    Some payments were paid but not recorded in the accounts. This usually means the account
                    settings are incomplete: open Payroll Settings and fill in the Salary Expense account and the
                    Cash or Bank account used for payments (and the Tax Payable account if tax is deducted), then
                    press Retry Post on each payment.
                </Alert>
            )}

            <TableContainer component={Paper} data-help-section="payroll.recon.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.recon.col.employee">Employee</TableCell>
                            <TableCell align="right" data-help="payroll.recon.col.amount">Amount</TableCell>
                            <TableCell data-help="payroll.recon.col.date">Payment Date</TableCell>
                            <TableCell data-help="payroll.recon.col.method">Method</TableCell>
                            <TableCell data-help="payroll.recon.col.journal">Journal Status</TableCell>
                            <TableCell align="right" data-help="payroll.recon.col.attempts">Attempts</TableCell>
                            <TableCell data-help="payroll.recon.col.error">Last Error</TableCell>
                            <TableCell data-help="payroll.recon.col.lastTry">Last Try</TableCell>
                            <TableCell align="right" data-help="payroll.recon.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">Loading...</TableCell>
                            </TableRow>
                        )}
                        {!loading && payments.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    No payments requiring reconciliation
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && payments.map(p => {
                            const isPosted = p.journalPostStatus === 'POSTED';
                            const showRetry = canCreatePayment && !isPosted;
                            const showSkip = canOverride && !isPosted;
                            return (
                                <TableRow key={p.id}>
                                    <TableCell>{p.employeeName || 'Unknown employee'}</TableCell>
                                    <TableCell align="right">{formatMoney(p.amount)}</TableCell>
                                    <TableCell>{formatDate(p.paymentDate)}</TableCell>
                                    <TableCell>{humanizeEnum(p.paymentMethod)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            data-help="payroll.recon.journalChip"
                                            size="small"
                                            label={humanizeEnum(p.journalPostStatus)}
                                            color={STATUS_COLORS[p.journalPostStatus]}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{p.journalPostAttempts}</TableCell>
                                    <TableCell>
                                        <Tooltip title={p.journalPostError || ''}>
                                            <span>{truncate(p.journalPostError)}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{p.journalPostLastTry ? formatDateTime(p.journalPostLastTry) : '-'}</TableCell>
                                    <TableCell align="right">
                                        {showRetry && (
                                            <Button
                                                data-help="payroll.recon.retry"
                                                size="small"
                                                onClick={() => handleRetry(p.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                Retry Post
                                            </Button>
                                        )}
                                        {showSkip && (
                                            <Button
                                                data-help="payroll.recon.skip"
                                                size="small"
                                                color="warning"
                                                onClick={() => setSkipTargetId(p.id)}
                                            >
                                                Skip
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            <SkipJournalDialog
                open={skipTargetId !== null}
                onClose={() => setSkipTargetId(null)}
                paymentId={skipTargetId || ''}
                onSuccess={load}
            />
        </Box>
    );
};

export default PaymentReconciliation;
